'use client';

import type React from 'react';

import { useIntersectionObserver } from '@/hooks/use-intersection-observer';

type AnimatedSectionProps = {
  children: React.ReactNode;
  className?: string;
  animation?: 'fade-in' | 'slide-up' | 'scale-in';
  threshold?: number;
  delay?: number;
  role?: string;
  id?: string;
  // SEO: minimal support for microdata
  itemScope?: boolean;
  itemProp?: string;
  itemType?: string;
};

export function AnimatedSection({
  children,
  className = '',
  animation = 'fade-in',
  threshold = 0.1,
  delay = 0,
  role,
  id,
  itemScope,
  itemProp,
  itemType,
}: AnimatedSectionProps) {
  const { ref, isIntersecting } = useIntersectionObserver({ threshold });

  return (
    <section
      ref={ref}
      className={`transition-all duration-700 ease-out ${
        isIntersecting
          ? `animate-${animation} translate-y-0 scale-100 opacity-100`
          : 'translate-y-8 scale-95 opacity-0'
      } ${className}`}
      style={{
        transitionDelay: isIntersecting ? `${delay}ms` : '0ms',
      }}
      role={role}
      id={id}
      // Minimal SEO props
      {...(itemScope && { itemScope: true })}
      {...(itemProp && { itemProp })}
      {...(itemType && { itemType })}
    >
      {children}
    </section>
  );
}
